import type { ReactNode } from "react";

import { Code } from "@hightouch/htui";

import { StaticType, type VariableScope } from "src/formkit/components/types";

enum VariableType {
  CURRENT_TIMESTAMP = "current-timestamp",
  MODEL_ID = "model-id",
  MODEL_NAME = "model-name",
  SYNC_ID = "sync-id",
  SYNC_RUN_ID = "sync-run-id",
  OPERATION = "operation",
  MESSAGE_ID = "message-id",
  TRIGGER_EVENT_MESSAGE_ID = "trigger-event-message-id",
  TRIGGER_EVENT_TIMESTAMP = "trigger-event-timestamp",
  TRIGGER_EVENT_ORIGINAL_TIMESTAMP = "trigger-event-original-timestamp",
  TRIGGER_EVENT_SENT_AT = "trigger-event-sent-at",
  TRIGGER_EVENT_RECEIVED_AT = "trigger-event-received-at",
  USER_ID = "user-id",
  JOURNEY_ID = "journey-id",
  JOURNEY_NAME = "journey-name",
  NODE_ID = "node-id",
  NODE_NAME = "node-name",
}

type VariableSection =
  | "User"
  | "Model"
  | "Journey"
  | "Tile"
  | "Sync"
  | "Event"
  | "Time";

const VARIABLE_SECTION_ORDER: VariableSection[] = [
  "User",
  "Model",
  "Journey",
  "Tile",
  "Sync",
  "Event",
  "Time",
];

export type VariableOption = {
  description: ReactNode;
  label: string;
  section: VariableSection;
  type: StaticType;
  variable: VariableType;
};

export type VariableOptionSection<T extends VariableOption = VariableOption> = {
  label: VariableSection;
  options: T[];
};

const currentTimestampOption: VariableOption = {
  description: (
    <>
      The current timestamp as an ISO 8601 string (
      <Code>"YYYY-MM-DDTHH:mm:ss.sssZ"</Code>).
    </>
  ),
  label: "Current timestamp",
  section: "Time",
  type: StaticType.DATETIME,
  variable: VariableType.CURRENT_TIMESTAMP,
};

// A journey trigger has no sync run or model of its own. It carries the user and the journey step that fired.
export const journeyVariableOptions: VariableOption[] = [
  {
    description: <>The ID of the user this tile fired for, as a string.</>,
    label: "User ID",
    section: "User",
    type: StaticType.STRING,
    variable: VariableType.USER_ID,
  },
  {
    description: <>The ID of the current journey as a string.</>,
    label: "Journey ID",
    section: "Journey",
    type: StaticType.STRING,
    variable: VariableType.JOURNEY_ID,
  },
  {
    description: <>The name of the current journey as a string.</>,
    label: "Journey name",
    section: "Journey",
    type: StaticType.STRING,
    variable: VariableType.JOURNEY_NAME,
  },
  {
    description: <>The ID of this tile as a string.</>,
    label: "Tile ID",
    section: "Tile",
    type: StaticType.STRING,
    variable: VariableType.NODE_ID,
  },
  {
    description: <>The name of this tile as a string.</>,
    label: "Tile name",
    section: "Tile",
    type: StaticType.STRING,
    variable: VariableType.NODE_NAME,
  },
  {
    description: <>A unique ID for this send as a string.</>,
    label: "Message ID",
    section: "Tile",
    type: StaticType.STRING,
    variable: VariableType.MESSAGE_ID,
  },
  currentTimestampOption,
];

// The formkit context can name the scope outright. Otherwise the model decides between a batch and a realtime
// audience sync.
export function resolveVariableScope({
  variableScope,
  model,
}: {
  variableScope?: VariableScope;
  model?: { is_realtime_audience?: boolean | null } | undefined;
}): VariableScope {
  return (
    variableScope ??
    (model?.is_realtime_audience ? "realtime-audience" : "sync")
  );
}

const realtimeEventVariableOptions: VariableOption[] = [
  {
    description: (
      <>The messageId of the audience membership event being sent.</>
    ),
    label: "Message ID",
    section: "Event",
    type: StaticType.STRING,
    variable: VariableType.MESSAGE_ID,
  },
  {
    description: (
      <>
        Message ID of the specific event that triggered the audience membership
        change.
      </>
    ),
    label: "Trigger Event Message ID",
    section: "Event",
    type: StaticType.STRING,
    variable: VariableType.TRIGGER_EVENT_MESSAGE_ID,
  },
  {
    description: (
      <>
        Timestamp of the specific event that triggered the audience membership
        change.
      </>
    ),
    label: "Trigger Event Timestamp",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_TIMESTAMP,
  },
  {
    description: (
      <>
        OriginalTimestamp of the specific event that triggered the audience
        membership change.
      </>
    ),
    label: "Trigger Event OriginalTimestamp",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_ORIGINAL_TIMESTAMP,
  },
  {
    description: (
      <>
        SentAt timestamp of the specific event that triggered the audience
        membership change.
      </>
    ),
    label: "Trigger Event SentAt",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_SENT_AT,
  },
  {
    description: (
      <>
        ReceivedAt timestamp of the specific event that triggered the audience
        membership change.
      </>
    ),
    label: "Trigger Event ReceivedAt",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_RECEIVED_AT,
  },
];

const batchSyncRunVariableOptions: VariableOption[] = [
  {
    description: <>The ID of the current sync run as an integer.</>,
    label: "Sync run ID",
    section: "Sync",
    type: StaticType.NUMBER,
    variable: VariableType.SYNC_RUN_ID,
  },
  {
    description: (
      <>
        The CDC operation <Code>add</Code>, <Code>change</Code>,
        <Code>remove</Code>, or <Code>all</Code> as a string.
      </>
    ),
    label: "Operation",
    section: "Sync",
    type: StaticType.STRING,
    variable: VariableType.OPERATION,
  },
];

function getSyncVariableOptions(isRealtimeSync: boolean): VariableOption[] {
  return [
    {
      description: <>The ID of the current model as a string.</>,
      label: "Model ID",
      section: "Model",
      type: StaticType.STRING,
      variable: VariableType.MODEL_ID,
    },
    {
      description: <>The name of the current model as a string.</>,
      label: "Model name",
      section: "Model",
      type: StaticType.STRING,
      variable: VariableType.MODEL_NAME,
    },
    {
      description: <>The ID of the current sync as an integer.</>,
      label: "Sync ID",
      section: "Sync",
      type: StaticType.NUMBER,
      variable: VariableType.SYNC_ID,
    },
    currentTimestampOption,
    ...(isRealtimeSync
      ? realtimeEventVariableOptions
      : batchSyncRunVariableOptions),
  ];
}

export function getVariableOptions({
  scope,
}: {
  scope: VariableScope;
}): VariableOption[] {
  return scope === "realtime-journey"
    ? journeyVariableOptions
    : getSyncVariableOptions(scope === "realtime-audience");
}

function groupBySection<T extends VariableOption>(
  options: T[],
): VariableOptionSection<T>[] {
  return VARIABLE_SECTION_ORDER.flatMap((label) => {
    const sectionOptions = options.filter((option) => option.section === label);
    return sectionOptions.length ? [{ label, options: sectionOptions }] : [];
  });
}

export function getVariableSections(
  scope: VariableScope,
): VariableOptionSection[] {
  return groupBySection(getVariableOptions({ scope }));
}
